import React, {
  Dispatch,
  FormEventHandler,
  SetStateAction,
  useMemo,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import { DerivedTextInput } from 'src/components/Reusable/Inputs'
import { ModalForm, ModalFormId } from 'src/components/ModalForms'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useFileTypeFilterContext } from 'src/pages/Evidence/context/FileTypeFilterContext'
import { useAppliedFilters } from 'src/Utils/globalHooks'
import { FilterParam } from '../FilterBar.types'
import {
  CustomFileIcon,
  getFileTypeFilterItem,
} from '../FilterDropdowns/EvidenceTypeFilterDropdown'

type CustomFileTypeFormDataType = {
  extension: string
}

export const CustomFileTypeFilterModal = (): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const show = useIsShowModal(ModalFormId.CUSTOM_FILE_TYPE_FILTER)
  const searchParams = useMemo(() => {
    return queryString.parse(location.search, {
      arrayFormat: 'bracket',
    })
  }, [location])
  const [newFilter, setNewFilter] = React.useState<CustomFileTypeFormDataType>({
    extension: '',
  })
  const [error, setError] = useState<string>('')
  const onHide = () => {
    setNewFilter({ extension: '' })
    setError('')
  }
  const hide = useHideModal({
    modalId: ModalFormId.CUSTOM_FILE_TYPE_FILTER,
    onHide,
  })
  const { filterValues, setFilterValues } = useFileTypeFilterContext()
  const validateExtension = (extension: string) => {
    if (!extension.trim().length) {
      setError('Extension cannot be empty')
      return false
    }
    if (extension.length > 10) {
      setError('Extension must be 10 characters or less')
      return false
    }
    if (extension.includes(' ')) {
      setError('Extension cannot contain spaces')
      return false
    }
    return true
  }
  const appliedFilters = useAppliedFilters(FilterParam.FILE_TYPE)

  const onSubmit: FormEventHandler<HTMLFormElement> = (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault()
    const currentFilters = new Set(filterValues?.map((filter) => filter.value))
    let extension = newFilter.extension
    const isValid = validateExtension(extension)
    if (!isValid) {
      return
    }
    if (!extension.startsWith('.')) {
      extension = `.${extension}`
    }
    if (!currentFilters.has(extension)) {
      setFilterValues((prev) => {
        return [
          ...prev,
          getFileTypeFilterItem(extension, extension, CustomFileIcon),
        ]
      })
    }

    navigate(
      queryString.stringifyUrl(
        {
          url: location.pathname,
          query: {
            ...searchParams,
            [FilterParam.FILE_TYPE]: [...appliedFilters, extension],
          },
        },
        { arrayFormat: 'bracket' },
      ),
    )
    hide()
  }

  return (
    <ModalForm
      title="Search for a Custom File Extension"
      description="Use this filter with a particular file extension, such as .docx"
      show={show}
      hide={hide}
      submitText="Filter"
      formId={ModalFormId.CUSTOM_FILE_TYPE_FILTER}
    >
      <form id={ModalFormId.CUSTOM_FILE_TYPE_FILTER} onSubmit={onSubmit}>
        <DerivedTextInput
          label="File extension"
          name={'extension'}
          initVal={newFilter.extension}
          isValid={!error}
          errorMessage={error}
          placeholder="Enter an extension"
          setFormData={
            setNewFilter as Dispatch<SetStateAction<{ [key: string]: string }>>
          }
          required
        />
      </form>
    </ModalForm>
  )
}
